import type { SelectChangeEvent } from '@mui/material'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import {
  Box,
  CircularProgress,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  Tooltip,
} from '@mui/material'
import CompPlanContent from 'components/CompPlanContent'
import SimpleTitle from 'components/SimpleTitle'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { DATE_FORMAT } from 'types/periods'
import useAuth from 'hooks/useAuth'
import { useFetchLedgerAccounts } from 'hooks/useLedgerAccounts'
import { useFetchPlansWithAccruals } from 'hooks/usePlans'
import { useFetchUser, useSendUserPlan } from 'hooks/useUsers'
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { LedgerAccount } from 'types/ledger-account'
import { UserProfile, hasFeature } from 'types/user-profile'
import EmployeeDashboard from '../EmployeeDashboard'

dayjs.extend(utc)

const EmployeePlanView: React.FC = () => {
  const theme = useTheme()
  const { token, profile } = useAuth()
  const { userId: paramUserId } = useParams()
  const navigate = useNavigate()

  const [view, setView] = useState('dashboard')
  const [rootOrganizationId, setRootOrganizationId] = useState('')
  const [profileInitialized, setProfileInitialized] = useState(false)
  const [compPlanInitialized, setCompPlanInitialized] = useState(false)
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccount[] | undefined>([])
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [lastSendTime, setLastSendTime] = useState<number>(0)
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)

  const { userProfile, isLoading: userLoading } = useFetchUser(
    token != null ? token : '',
    paramUserId!,
    null,
    paramUserId != null && token != null
  )

  const {
    compPlans,
    isLoading: planLoading,
    isError: isErrorPlans,
  } = useFetchPlansWithAccruals(token != null ? token : '', userProfile?.user?.id || '', null)

  const { sendUserPlan, isLoading: isSendingPlan } = useSendUserPlan(token!)

  const { ledgerAccounts: fetchedLedgerAccounts, isLoading: isLoadingLedgerAccounts } = useFetchLedgerAccounts(
    rootOrganizationId,
    token != null ? token : '',
    rootOrganizationId !== '' && token !== null && compPlanInitialized
  )

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: string) => {
    if (newView !== null) {
      setView(newView)
    }
  }

  const handlePlanChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value
    const selectedPlan = userProfile?.plans?.find((p) => p.id === selectedId)
    if (selectedPlan) {
      setSelectedPlanId(selectedId)
      setRootOrganizationId(selectedPlan.rootOrganizationId || '')
    }
  }

  const checkButtonAvailability = () => {
    const currentTime = Date.now()
    const timeDifference = currentTime - lastSendTime
    const oneMinute = 60 * 1000 // 1 minute in milliseconds

    if (timeDifference < oneMinute) {
      setIsButtonDisabled(true)
      const remainingTime = oneMinute - timeDifference
      setTimeout(() => {
        setIsButtonDisabled(false)
      }, remainingTime)
    } else {
      setIsButtonDisabled(false)
    }
  }

  useEffect(() => {
    if (lastSendTime > 0) {
      checkButtonAvailability()
    }
  }, [lastSendTime])

  const isLoading = userLoading || planLoading || isLoadingLedgerAccounts || !profileInitialized

  useEffect(() => {
    if (userProfile?.plans && userProfile.plans.length > 0) {
      const firstPlan = userProfile.plans[0]
      setSelectedPlanId(firstPlan.id || null)
    }
  }, [userProfile?.plans])

  useEffect(() => {
    if (userProfile && !profileInitialized) {
      setView('dashboard')
      setProfileInitialized(true)
    }
  }, [userProfile, profileInitialized])

  useEffect(() => {
    if (compPlans && compPlans.plans.length > 0) {
      setRootOrganizationId(compPlans.plans[0].plan.rootOrganizationId)
      setCompPlanInitialized(true)
    }
  }, [compPlans])

  useEffect(() => {
    if (fetchedLedgerAccounts) {
      setLedgerAccounts(fetchedLedgerAccounts)
    }
  }, [fetchedLedgerAccounts])

  useEffect(() => {
    if (isErrorPlans) {
      navigate('/access-error')
    }
  }, [isErrorPlans, navigate])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  const title = (profile: UserProfile | undefined) => {
    if (!profile) {
      return ''
    }

    if (view === 'dashboard') {
      return `${profile.user.firstName} ${profile.user.lastName}'s Dashboard`
    } else {
      return `${profile.user.firstName} ${profile.user.lastName}'s Compensation Plan`
    }
  }

  const renderContent = () => {
    if (!compPlans || !userProfile) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <div>Error: Unable to load user data</div>
        </Box>
      )
    }

    const selectedPlan = compPlans.plans.find((plan) => plan.plan.id === selectedPlanId)

    if (view === 'dashboard') {
      return (
        <EmployeeDashboard
          key="employee-dashboard"
          user={userProfile?.user}
          plan={compPlans?.plans || []}
          selectedPlan={selectedPlan}
          theme={theme}
          status={isErrorPlans ? 400 : 200}
        />
      )
    } else {
      return (
        <>
          {selectedPlan ? (
            <CompPlanContent user={userProfile?.user} compPlan={selectedPlan} ledgerAccounts={ledgerAccounts} />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <div>No plan selected</div>
            </Box>
          )}
        </>
      )
    }
  }

  const sendPlanToEmployee = () => {
    if (isSendingPlan || isButtonDisabled) return
    if (!paramUserId) {
      console.error('User ID is missing')
      return
    }

    const currentTime = Date.now()
    setLastSendTime(currentTime)
    setIsButtonDisabled(true)

    console.log('Send Invite clicked for user:', paramUserId)
    sendUserPlan(paramUserId)

    // Set timeout for 1 minute
    setTimeout(() => {
      setIsButtonDisabled(false)
    }, 60 * 1000)
  }

  return (
    <div style={{ position: 'relative' }}>
      <Grid container pb={2} spacing={2}>
        {/* Title - Full width on mobile, adjusts based on period selector presence */}
        <Grid size={{ xs: 12, md: userProfile?.plans && userProfile.plans.length > 1 ? 7 : 9 }}>
          <SimpleTitle title={title(userProfile)} />
        </Grid>

        {/* Period Selector - Full width on mobile, 2 columns on desktop */}
        {userProfile?.plans && userProfile.plans.length > 1 && (
          <Grid
            size={{ xs: 12, md: 2 }}
            sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 0, md: 1 } }}
          >
            <FormControl variant="outlined" fullWidth>
              <InputLabel
                shrink
                variant="outlined"
                sx={{
                  '&.MuiInputLabel-shrink': {
                    background: theme.palette.background.default,
                  },
                }}
              >
                Select Plan
              </InputLabel>
              <Select
                labelId="plan-select-label"
                id="plan-select"
                variant="outlined"
                value={selectedPlanId || ''}
                label="Select Plan"
                style={{ borderRadius: '21px' }}
                onChange={handlePlanChange}
              >
                {userProfile.plans.map((p) => (
                  <MenuItem key={p.id || ''} value={p.id || ''}>
                    {`${dayjs.utc(p.effectiveDate).format(DATE_FORMAT)} - ${
                      p.endDate ? dayjs.utc(p.endDate).format(DATE_FORMAT) : 'Present'
                    }`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Dashboard/Plan Toggle - Full width on mobile, 3 columns on desktop to match compensation card width */}
        <Grid
          size={{ xs: 12, md: 3 }}
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            alignItems: 'center',
          }}
        >
          {hasFeature(profile!, 'LaunchFeatures') && (
            <Box sx={{ mr: 1 }}>
              <Tooltip title={isButtonDisabled ? 'Please wait before sending again' : 'Share plan with employee'}>
                <span>
                  <IconButton
                    onClick={sendPlanToEmployee}
                    size="small"
                    aria-label="Send plan to employee"
                    disabled={isButtonDisabled || isSendingPlan}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '4px',
                      width: 32,
                      height: 32,
                      opacity: isButtonDisabled || isSendingPlan ? 0.5 : 1,
                    }}
                  >
                    <IosShareOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
          {compPlans?.plans?.length > 0 && (
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleViewChange}
              aria-label="View toggle"
              size="small"
              sx={{ width: { xs: '100%', md: '100%' }, height: 32 }}
            >
              <ToggleButton value="dashboard" aria-label="Dashboard View" sx={{ width: '50%', px: 2 }}>
                Dashboard
              </ToggleButton>
              <ToggleButton value="plan" aria-label="Plan View" sx={{ width: '50%', px: 2 }}>
                Plan
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Grid>
      </Grid>

      {renderContent()}
    </div>
  )
}

export default EmployeePlanView
